import { TypedArrayUtils } from '@seddi/core';
import { Seam } from '@seddi/geometry';
import {
  type PieceGeometry,
  type Scene,
  type SceneEntity,
  type SeamComponent,
  type SeamSide,
  GarmentEntities,
  PiecesLinesPaths,
  StitchOrder,
} from '@seddi/scene';
import { SeamBuilder } from '@seddi/scene-builder';
import { type Object3D, BufferGeometry, LineBasicMaterial, LineSegments } from '@seddi/three';

import { assert } from '../core/assert';
import { createVertexAttributeF32, updateVertexAttributeF32 } from '../core/bufferGeometry';
import type { GarmentGeometryGfx } from '../resources/garmentGeometryTypes';
import type { ResourcesContext } from '../resources/resourcesContext';
import { SceneGeometryModule } from '../scene/sceneGeometryModule';
import { selectColorByIndex } from '../theme/themeColors';
import type { Theme } from '../theme/themeTypes';
import {
  type SeamTapeRenderable,
  attachGarmentGeometry as attachGarmentGeometryTape,
  detachGarmentGeometry as detachGarmentGeometryTape,
  dispose as disposeTape,
} from './seamTapeRenderable';
import { type StitchRenderable, create as createStitch, dispose as disposeStitch } from './stitchRenderable';

type Stitch = StitchRenderable & { name: string; order: StitchOrder };

export type SeamRenderable = {
  readonly id: string;
  readonly geometry: BufferGeometry;
  readonly material: LineBasicMaterial;
  readonly mesh: LineSegments;
  readonly parent: Object3D;
  readonly stitches: Stitch[];
  tape?: SeamTapeRenderable;
  readonly useStitchesDecals: boolean;
};

/**
 * Create renderable.
 * @param id - ID.
 * @param node - Node.
 * @param seam - Seam state.
 * @param theme - Theme.
 * @param useStitchesDecals - Use stitches decals.
 */
export const create = (
  id: string,
  parent: Object3D,
  seam: SeamComponent,
  theme: Theme,
  useStitchesDecals: boolean,
): SeamRenderable => {
  const geometry = new BufferGeometry();
  // Create lines material.
  const color = selectColorByIndex(theme.threads, seam.color);
  const material = new LineBasicMaterial();
  material.color.set(color).convertSRGBToLinear();
  // Create lines mesh.
  const mesh = new LineSegments(geometryPlaceholder, material);
  mesh.name = `${id}-mesh`;
  parent.add(mesh);
  return { geometry, id, material, mesh, parent, stitches: [], useStitchesDecals };
};

/**
 * Dispose.
 * @param self - Seam renderable.
 * @param resources - Resources context.
 */
export const dispose = (self: SeamRenderable, resources: ResourcesContext) => {
  // Dispose seam stitches.
  removeStitches(self);
  // Dispose seam tape.
  if (self.tape) {
    disposeTape(self.tape, resources);
    delete self.tape;
  }
  self.geometry.dispose();
  self.material.dispose();
  const mesh = self.mesh;
  mesh.parent.remove(mesh);
};

// ------ GARMENT GEOMETRY ------

/**
 * Update seam geometry from high-resolution geometry.
 * @param self - Seam renderable.
 * @param desc - Seam state.
 * @param garmentGeometry - Garment geometry resource.
 * @param resources - Resources context.
 */
export const attachGarmentGeometry = (
  self: SeamRenderable,
  desc: SeamComponent,
  garmentGeometry: GarmentGeometryGfx,
  resources: ResourcesContext,
) => {
  const { id, mesh, tape } = self;
  // Attach to mesh.
  const sewingOperation = garmentGeometry.sewingOperations.get(id)!;
  assert(sewingOperation, `Sewing operation ${id} not found`);
  mesh.geometry = sewingOperation.geometry;
  // Attach to stitches.
  createStitches(self, desc, garmentGeometry, resources);
  // Attach to tape.
  tape && attachGarmentGeometryTape(tape, garmentGeometry, resources);
};

/**
 * Detach garment geometry.
 * @param self - Seam tape renderable.
 * @param resources - Resources context;
 */
export const detachGarmentGeometry = (self: SeamRenderable, resources: ResourcesContext) => {
  const { mesh, tape } = self;
  // Detach from mesh.
  mesh.geometry = geometryPlaceholder;
  // Detach from stitches.
  removeStitches(self);
  // Detach from tape.
  tape && detachGarmentGeometryTape(tape, resources);
};

// ------ GEOMETRY ------

/**
 * Create lines geometry from sides vertices. Used by garment geometry loader.
 * @param sides Sides vertices.
 */
export const createLinesGeometry = (
  sides: Float32Array[][],
  options?: { computeBoundingSphere?: boolean },
): BufferGeometry => {
  // Compute vertex data from sides.
  let position = createVerticesDataSides(sides);

  // Compute vertex data from glue lines.
  if (sides.length > 1) {
    const positionGlue = createVerticesDataGlue(sides);
    position = TypedArrayUtils.Concat([position, positionGlue]);
  }

  // Create non-indexed geometry.
  const geometry = new BufferGeometry();
  createVertexAttributeF32(geometry, 'position', position, 3);

  // Recompute bounding sphere at runtime.
  if (options?.computeBoundingSphere) {
    geometry.computeBoundingSphere();
  }

  return geometry;
};

/**
 * Update lines geometry from scene state.
 * @param self - Seam renderable.
 * @param seam - Seam state.
 * @param scene - Scene state.
 * @param options - Update geometry options.
 */
export const updateLinesGeometryFromScene = (
  self: SeamRenderable,
  seam: SeamComponent,
  scene: Scene,
  options?: { computeBoundingSphere?: boolean },
) => {
  // Discretize seam sides from scene.
  const pieces = GarmentEntities.PiecesFromSeamSides(scene.children, seam.lines);
  // NOTE: Batch updates should guarantee this never happens.
  if (!pieces) return;

  // Compute vertex data from pieces.
  const sides = discretizeSidesFromPieces(seam.lines, pieces, scene.resolution);
  // Compute vertex data from sides.
  let position = createVerticesDataSides(sides);
  // Compute vertex data from glue lines.
  if (sides.length > 1) {
    const positionGlue = createVerticesDataGlue(sides);
    position = TypedArrayUtils.Concat([position, positionGlue]);
  }

  // Update geometry in-place.
  const { geometry } = self;
  updateVertexAttributeF32(geometry, 'position', position, 3);
  // Recompute bounding sphere at runtime.
  if (options?.computeBoundingSphere) {
    geometry.computeBoundingSphere();
  }

  // Update mesh.
  if (self.mesh.geometry !== geometry) {
    self.mesh.geometry = geometry;
  }
};

/**
 * Vertices positions for seam glue, lines are drawn between pairs of vertices.
 * Future improvement could be to generate index buffer data and avoid vertex duplicates.
 * @param sides Seam sides vertices.
 */
const createVerticesDataGlue = (sides: Float32Array[][]): Float32Array => {
  const side0 = sides[0];
  const side1 = sides[1];
  const length = side0.reduce((n, segments) => n + segments.length, 0) * 2;
  const data = new Float32Array(length);
  let offset = 0;

  for (let s = 0; s < side0.length; s++) {
    const vertices0 = side0[s];
    const vertices1 = side1[s];

    for (let i = 0; i < vertices0.length; i += 3) {
      data[offset] = vertices0[i];
      data[offset + 1] = vertices0[i + 1];
      data[offset + 2] = vertices0[i + 2];
      data[offset + 3] = vertices1[i];
      data[offset + 4] = vertices1[i + 1];
      data[offset + 5] = vertices1[i + 2];
      offset += 6;
    }
  }

  return data;
};

/**
 * Vertices positions for seam sides, lines are drawn between pairs of vertices.
 * Future improvement could be to generate index buffer data and avoid vertex duplicates.
 * @param sides Seam sides vertices.
 */
const createVerticesDataSides = (sides: Float32Array[][]): Float32Array => {
  let length = 0;

  for (const segments of sides) {
    for (const vertices of segments) {
      length += vertices.length * 2 - 6;
    }
  }

  const data = new Float32Array(length);
  let offset = 0;

  for (const segments of sides) {
    for (const vertices of segments) {
      for (let i = 0; i < vertices.length - 3; i += 3) {
        data[offset] = vertices[i];
        data[offset + 1] = vertices[i + 1];
        data[offset + 2] = vertices[i + 2];
        data[offset + 3] = vertices[i + 3];
        data[offset + 4] = vertices[i + 4];
        data[offset + 5] = vertices[i + 5];
        offset += 6;
      }
    }
  }

  return data;
};

/**
 * Construct seam vertices from piece triangulation.
 * @param sides Seam sides.
 * @param pieces Seam pieces.
 * @param resolution Scene resolution.
 */
const discretizeSidesFromPieces = (
  sides: SeamSide[],
  pieces: SceneEntity[],
  resolution: number = 0.01,
): Float32Array[][] => {
  const cellSize = resolution * 1.5;

  const geometryCache = SceneGeometryModule.instance.geometryCache;
  const geometryMap = new Map<string, PieceGeometry>();

  for (const piece of pieces) {
    const {
      _id,
      components: { garmentPiece },
    } = piece;

    // Update 2D query acceleration structures.
    const pieceCache = geometryCache.touchPiece(_id);
    pieceCache.updateGrid(garmentPiece.geometry.indices, garmentPiece.geometry.position2D, cellSize);

    geometryMap.set(_id, garmentPiece.geometry);
  }

  // Construct seam.
  const paths = new PiecesLinesPaths();
  paths.build(pieces);
  const seam = new Seam();
  const builder = new SeamBuilder(paths);
  builder.build(sides, seam);

  const vertices = [];

  for (const side of seam.sides) {
    const sideVertices = [];

    for (const segment of side.segments) {
      const { direction, length, path, piece } = segment as any;

      // Sample vertices from seam sides.
      const pieceCache = geometryCache.pieces.get(piece);
      const pieceGeometry = geometryMap.get(piece);
      const is = pieceGeometry.indices;
      const vs = pieceGeometry.position3D;

      const nSegments = Math.floor((length + 0.01 - 1e-6) / 0.01);
      const h = 1 / nSegments;
      const segmVertices = new Float32Array((nSegments + 1) * 3);

      for (let i = 0; i <= nSegments; i++) {
        // Sample path by distance.
        const pt = path.normalizedDistanceToPoint(h * i);
        const p2 = path.eval(pt);

        // Accelerated point inside triangle-mesh query.
        const res = pieceCache.grid.query(p2);
        const { triangle: f, coordinates: c } = res;

        // Reverse output position if direction is false.
        const o = direction ? i * 3 : (nSegments - i) * 3;

        // Apply interpolation.
        const f3 = f * 3;
        const i0 = is[f3 + 0] * 3;
        const i1 = is[f3 + 1] * 3;
        const i2 = is[f3 + 2] * 3;
        segmVertices[o + 0] = c.x * vs[i0 + 0] + c.y * vs[i1 + 0] + c.z * vs[i2 + 0];
        segmVertices[o + 1] = c.x * vs[i0 + 1] + c.y * vs[i1 + 1] + c.z * vs[i2 + 1];
        segmVertices[o + 2] = c.x * vs[i0 + 2] + c.y * vs[i1 + 2] + c.z * vs[i2 + 2];
      }

      sideVertices.push(segmVertices);
    }

    vertices.push(sideVertices);
  }

  return vertices;
};

// ------ STITCHES ------

/**
 * Create garment geometry stitches.
 * @param self - Seam renderable.
 * @param seam - Seam state.
 * @param garmentGeometry - Garment geometry resource.
 * @param resources - Resources context.
 */
const createStitches = (
  self: SeamRenderable,
  seam: SeamComponent,
  garmentGeometry: GarmentGeometryGfx,
  resources: ResourcesContext,
) => {
  const { id, parent, stitches } = self;
  // Find the sewing operation for the current seam.
  const sewingOperation = garmentGeometry.sewingOperations.get(id)!;

  for (const { geometry, name, order } of sewingOperation.stitches) {
    const processStitch = seam.process[convertStitchOrderToProp(order)];
    const { color } = processStitch[processStitch.kind][name];
    const stitch = createStitch(parent, geometry, color, resources) as Stitch;
    stitch.mesh.name = `${id}-stitch-${order}-${name}`;
    stitch.name = name;
    stitch.order = order;
    stitches.push(stitch);
  }
};

/**
 * Remove stitches.
 * @param self - Seam renderable.
 */
const removeStitches = (self: SeamRenderable) => {
  for (const stitch of self.stitches) {
    disposeStitch(stitch);
  }
  self.stitches.length = 0;
};

/**
 * Convert stitch order to process prop.
 * @param order Stitch order.
 */
const convertStitchOrderToProp = (order: StitchOrder): 'stitch' | 'stitchSec' => {
  if (order === StitchOrder.SECONDARY) return 'stitchSec';

  return 'stitch';
};

// ------ PLACEHOLDERS ------

// Placeholders are defined in global context for now.
const geometryPlaceholder = new BufferGeometry();
